import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { analytics } from '@heycatch/sdk';
import { setCurrentUserId } from '../lib/currentUser';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const initializedRef = useRef(false);
  // Store token in memory only (not localStorage) for better security
  const tokenRef = useRef(null);

  // Create api instance with useMemo to prevent recreation
  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: `${API_URL}/api`,
      withCredentials: true,  // Send httpOnly cookies automatically
    });
    
    // Add request interceptor - token is primarily sent via httpOnly cookie
    // Memory token is a backup for immediate use after login
    instance.interceptors.request.use((config) => {
      if (tokenRef.current) {
        config.headers.Authorization = `Bearer ${tokenRef.current}`;
      }
      return config;
    });
    
    // Add response interceptor to handle auth errors and token refresh
    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        
        // If 401 and we haven't tried refreshing yet
        if (error.response?.status === 401 && !originalRequest._retry) {
          const url = error.config?.url || '';
          
          // Don't refresh on auth endpoints
          if (!url.includes('/auth/login') && !url.includes('/auth/register') && !url.includes('/auth/refresh')) {
            originalRequest._retry = true;
            
            try {
              // Try to refresh the token using httpOnly refresh cookie
              const refreshResponse = await instance.post('/auth/refresh');
              tokenRef.current = refreshResponse.data.token;
              
              // Retry the original request
              originalRequest.headers.Authorization = `Bearer ${tokenRef.current}`;
              return instance(originalRequest);
            } catch (refreshError) {
              // Refresh failed - clear auth state
              tokenRef.current = null;
              setUser(null);
              if (process.env.NODE_ENV === 'development') console.warn('Token refresh failed, user logged out');
            }
          }
        }
        return Promise.reject(error);
      }
    );
    
    return instance;
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      // httpOnly cookie will be sent automatically
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Auth check failed:', error);
      if (error.response?.status === 401) {
        tokenRef.current = null;
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    setCurrentUserId(user?.user_id || null);
  }, [user]);

  // Identify/reset HeyCatch identity in sync with auth state — covers
  // login, register, Google session, and page-refresh session restore.
  useEffect(() => {
    if (loading) return;
    if (user) {
      analytics.setIdentity(
        user.user_id,
        { email: user.email, name: user.name, plan: user.subscription_plan },
        { signup_date: user.created_at }
      );
    } else {
      analytics.resetIdentity();
    }
  }, [user, loading]);

  useEffect(() => {
    // Prevent double initialization in StrictMode
    if (initializedRef.current) return;
    initializedRef.current = true;
    
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, rememberMe = true) => {
    const response = await api.post('/auth/login', { email, password, remember_me: rememberMe });
    const { token: newToken, ...userData } = response.data;
    // Store token in memory only (httpOnly cookie is primary auth)
    tokenRef.current = newToken;
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password, referralCode = null) => {
    const payload = { name, email, password };
    if (referralCode) {
      payload.referral_code = referralCode;
    }
    const response = await api.post('/auth/register', payload);
    const { token: newToken, ...userData } = response.data;
    tokenRef.current = newToken;
    setUser(userData);
    return userData;
  };

  const processGoogleSession = async (sessionId) => {
    const response = await api.post('/auth/session', { session_id: sessionId });
    const { token: newToken, ...userData } = response.data;
    tokenRef.current = newToken;
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Logout error:', error);
    }
    tokenRef.current = null;
    setUser(null);
  };

  const logoutAll = async () => {
    try {
      await api.post('/auth/logout-all');
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Logout-all error:', error);
    }
    tokenRef.current = null;
    setUser(null);
  };

  const refreshUser = useCallback(async () => {
    await checkAuth();
  }, [checkAuth]);

  const value = useMemo(() => ({
    user,
    loading,
    login,
    register,
    logout,
    logoutAll,
    processGoogleSession,
    refreshUser,
    api,
    isAuthenticated: !!user
  }), [user, loading, api, refreshUser]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
